import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The page title lives in the app banner, where the search box used to be (the product owner's layout, 2026-09-25):
 * a screen renders its h1 through `InTitleSlot`, and the shell's `Header` owns the element it lands in. Outside the
 * shell (a screen rendered alone, as the UI tests do) there is no banner, so the title renders in place.
 *
 * `undefined` = no shell; `null` = the shell has not mounted its banner yet (the title waits one commit rather than
 * flashing inside the page first).
 */
const TitleSlotContext = createContext<HTMLElement | null | undefined>(undefined);

export const TitleSlotProvider = TitleSlotContext.Provider;

/**
 * How a title fits the banner (D41): below `sm` it wraps to at most two lines with snug leading, so a phone shows
 * "Hana của tôi" whole instead of "Hana của …"; from `sm` up it stays on one line, cut with an ellipsis (P2 F15). The
 * banner is 52 px high: two lines of 18 px at `leading-snug` take 49.5 px (`leading-tight` clips the Vietnamese marks by 1 px).
 */
export const BANNER_TITLE = 'max-sm:line-clamp-2 max-sm:text-ellipsis max-sm:leading-snug max-sm:wrap-anywhere sm:truncate';

export function InTitleSlot({ children }: { children: ReactNode }) {
  const slot = useContext(TitleSlotContext);
  if (slot === undefined) return <>{children}</>;
  return slot === null ? null : createPortal(children, slot);
}

/** Whether the title renders in the banner, so a screen can leave its in-page header row out when it holds nothing else. */
export function useTitleInBanner(): boolean {
  return useContext(TitleSlotContext) !== undefined;
}
